// The editor's working copy of a card's points, and the edits it can make to
// them. The server pins the first point to the start and the last to the
// finish and does all the arithmetic; this only keeps the list in walking
// order and shapes it for saveRouteCard.

export interface EditableCheckpoint {
  distanceMeters: number;
  name: string;
  note: string;
  escape: string;
  restMinutes: number;
}

export interface CardPoint {
  distanceMeters: number;
  name?: string | null;
  note?: string | null;
  escape?: string | null;
  restMinutes: number;
}

/** The rest choices a point offers, in minutes. */
export const REST_OPTIONS = [0, 15, 30, 45] as const;

/** Closest two points may sit, so a section never collapses to nothing. */
export const MIN_GAP_METRES = 10;

export function fromCardPoints(points: CardPoint[]): EditableCheckpoint[] {
  return points.map((point) => ({
    distanceMeters: point.distanceMeters,
    name: point.name ?? '',
    note: point.note ?? '',
    escape: point.escape ?? '',
    restMinutes: point.restMinutes ?? 0,
  }));
}

/** The saveRouteCard `checkpoints` argument. Blank text is sent as null. */
export function toCheckpointInput(points: EditableCheckpoint[]) {
  return points.map((point) => ({
    distanceMeters: Math.round(point.distanceMeters * 10) / 10,
    name: point.name.trim() || null,
    note: point.note.trim() || null,
    escape: point.escape.trim() || null,
    restMinutes: point.restMinutes,
  }));
}

const isEnd = (points: EditableCheckpoint[], index: number): boolean =>
  index === 0 || index === points.length - 1;

/** The stretch a point may be dragged within: between its two neighbours. */
export function dragRange(points: EditableCheckpoint[], index: number): [number, number] {
  return [
    points[index - 1].distanceMeters + MIN_GAP_METRES,
    points[index + 1].distanceMeters - MIN_GAP_METRES,
  ];
}

/** Move an in-between point along the route. The start and finish don't move. */
export function movePoint(points: EditableCheckpoint[], index: number, distance: number): EditableCheckpoint[] {
  if (isEnd(points, index)) return points;
  const [min, max] = dragRange(points, index);
  if (min > max) return points;
  return points.map((point, i) => (i === index
    ? { ...point, distanceMeters: Math.min(Math.max(distance, min), max) }
    : point));
}

/** Split a section in two with a new point half way along it. Returns the list and the new point's index. */
export function addPointInSection(points: EditableCheckpoint[], sectionIndex: number): {
  points: EditableCheckpoint[]; index: number;
} {
  const from = points[sectionIndex];
  const to = points[sectionIndex + 1];
  if (!from || !to || to.distanceMeters - from.distanceMeters < MIN_GAP_METRES * 2) {
    return { points, index: -1 };
  }
  const added: EditableCheckpoint = {
    distanceMeters: (from.distanceMeters + to.distanceMeters) / 2,
    name: '', note: '', escape: '', restMinutes: 0,
  };
  const next = [...points.slice(0, sectionIndex + 1), added, ...points.slice(sectionIndex + 1)];
  return { points: next, index: sectionIndex + 1 };
}

/** Remove an in-between point, joining its two sections. The start and finish stay. */
export function removePoint(points: EditableCheckpoint[], index: number): EditableCheckpoint[] {
  if (isEnd(points, index)) return points;
  return points.filter((_, i) => i !== index);
}

export function updatePoint(
  points: EditableCheckpoint[],
  index: number,
  patch: Partial<Omit<EditableCheckpoint, 'distanceMeters'>>,
): EditableCheckpoint[] {
  return points.map((point, i) => (i === index ? { ...point, ...patch } : point));
}
